/* ============================================================================
   Lab Effects — Quality & Compliance (COA & SDS) — front-end logic
   ----------------------------------------------------------------------------
   • Fetches the prebuilt compliance index ONCE (all 600+ SKUs), caches it,
     and does ALL search + category filtering in memory — so the full catalog
     is searchable with no per-keystroke network and no Shopify 250 cap.
   • Renders rows in BATCHES (windowing) so a 600-row catalog never blocks paint.
   • Colors each category pill (text + border + faint fill) from the
     product_category metaobject map emitted by the section — change a category
     color once in Shopify and every pill here updates. Index carries the
     category NAME only, never a hard-coded color.
   • Flavor Enhanced SKUs are split into TWO rows (CDT + BDT) by the index
     generator; each row carries its own COA/SDS and an optional variantLabel.
   • Opens COA / SDS / certificate / packet PDFs in an in-page viewer modal.

   INDEX SHAPE (assets/compliance-index.json) — generated at publish time:
     [{ "title":"Tickled Pink", "handle":"tickled-pink-…",
        "category":"Flavor Enhanced Strains", "variantLabel":"CDT",
        "url":"/products/tickled-pink-…?variant=123",
        "coaUrl":"https://…/coa.pdf", "sdsUrl":"https://…/sds.pdf" }, … ]
   See README for the cursor-paginated generator that walks past Shopify's 250 cap.
   ============================================================================ */
(function () {
  "use strict";
  var root = document.querySelector("[data-le-compliance]");
  if (!root) return;

  var BATCH = parseInt(root.dataset.batchSize, 10) || 75;
  var SHOW_AZ = root.dataset.showAz === "true";
  var PRODUCT_BASE = (root.dataset.productBase || "/products").replace(/\/$/, "");
  var LICENSE_MODE = root.dataset.licenseMode || "modal";

  var listEl = root.querySelector("[data-list]");
  var searchEl = root.querySelector("[data-search]");
  var searchWrap = root.querySelector(".le-search");
  var searchClear = root.querySelector("[data-search-clear]");
  var countEl = root.querySelector("[data-count]");
  var clearEl = root.querySelector("[data-clear]");
  var LOGO_ICON = root.dataset.logoIcon || "";
  var catMenu = root.querySelector("[data-cat-menu]");
  var catToggle = root.querySelector("[data-cat-toggle]");
  var catCount = root.querySelector("[data-cat-count]");
  var loadmoreWrap = root.querySelector("[data-loadmore]");
  var loadmoreBtn = root.querySelector("[data-loadmore-btn]");

  var ALL = [];
  var filtered = [];
  var rendered = 0;
  var query = "";
  var cats = [];

  /* ---- category color map (single source = product_category metaobjects) ---- */
  var CATMAP = {};
  (function () {
    var el = root.querySelector("[data-le-cat-map]");
    if (!el) return;
    try {
      JSON.parse(el.textContent || "[]").forEach(function (c) {
        if (!c) return;
        if (c.name) CATMAP[norm(c.name)] = c;
        if (c.acronym) CATMAP[norm(c.acronym)] = c; // also key by acronym (CDT/BDT/…)
      });
    } catch (e) { console.error("[compliance] category map parse failed:", e); }
  })();
  function norm(s) { return String(s || "").trim().toLowerCase(); }
  function catMeta(name) { return CATMAP[norm(name)] || null; }
  function catColor(name) { var m = catMeta(name); return (m && m.color) || "#36B5C4"; }
  function hexToRgba(hex, a) {
    var h = String(hex || "").replace("#", "");
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return "rgba(54,181,196," + a + ")";
    var r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return "rgba(" + r + "," + g + "," + b + "," + a + ")";
  }

  /* ---- cGMP small-c formatting ---- */
  function cgmp(text) {
    return (text || "").replace(/cGMP/g, '<span class="le-c">c</span>GMP');
  }
  document.querySelectorAll("[data-cert-code],[data-cert-full]").forEach(function (el) {
    el.innerHTML = cgmp(el.textContent);
  });

  /* ---- viewer modal ---- */
  var DOC_IC = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6"/><path d="M9 17h6"/></svg>';
  var viewer = document.createElement("div");
  viewer.className = "le-viewer";
  viewer.hidden = true;
  viewer.innerHTML =
    '<div class="le-viewer-bar">' +
    '<span class="le-viewer-ic" aria-hidden="true">' + DOC_IC + '</span>' +
    '<div class="le-viewer-meta"><h3 data-vtitle></h3><p class="le-viewer-sub" data-vsub hidden></p></div>' +
    '<button type="button" class="le-iconbtn" data-vprint title="Print"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9V3h12v6"/><path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><path d="M6 14h12v7H6z"/></svg></button>' +
    '<a class="le-iconbtn" data-vdownload title="Download" download><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M12 3v12"/><path d="M7 11l5 5 5-5"/><path d="M5 21h14"/></svg></a>' +
    '<a class="le-iconbtn" data-vopen target="_blank" rel="noopener" title="Open in new tab"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M14 4h6v6"/><path d="M20 4l-9 9"/><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg></a>' +
    '<button type="button" class="le-iconbtn le-iconbtn--close" data-vclose title="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
    '<iframe class="le-viewer-frame" data-vframe title="Document preview"></iframe>' +
    '<img class="le-viewer-img" data-vimg alt="" hidden>';
  document.body.appendChild(viewer);
  var vTitle = viewer.querySelector("[data-vtitle]");
  var vSub = viewer.querySelector("[data-vsub]");
  var vFrame = viewer.querySelector("[data-vframe]");
  var vImg = viewer.querySelector("[data-vimg]");
  var vDownload = viewer.querySelector("[data-vdownload]");
  var vOpen = viewer.querySelector("[data-vopen]");
  var currentUrl = "";

  // Images (cert scans, badge art) render at natural size inside an <iframe> —
  // a large scan then overflows and only a corner shows. Route those to a
  // contained <img> (object-fit:contain) instead; PDFs/everything else use the
  // iframe so the browser's PDF viewer handles paging.
  function isImageUrl(u) { return /\.(png|jpe?g|gif|webp|avif|svg|bmp|tiff?)(?:[?#]|$)/i.test(String(u || "")); }

  function openDoc(url, title, subtitle) {
    vTitle.innerHTML = cgmp(escapeHtml(title || "Document"));
    if (subtitle) { vSub.innerHTML = cgmp(escapeHtml(subtitle)); vSub.hidden = false; }
    else { vSub.textContent = ""; vSub.hidden = true; }
    currentUrl = url || "";
    if (url && isImageUrl(url)) {
      vImg.src = url; vImg.hidden = false;
      vFrame.hidden = true; vFrame.removeAttribute("src");
    } else {
      vImg.hidden = true; vImg.removeAttribute("src");
      vFrame.hidden = false;
      if (url) { vFrame.src = url; } else { vFrame.removeAttribute("src"); }
    }
    if (url) { vDownload.href = url; vOpen.href = url; }
    viewer.hidden = false;
    document.body.style.overflow = "hidden";
  }
  function closeDoc() { viewer.hidden = true; vFrame.removeAttribute("src"); vImg.removeAttribute("src"); document.body.style.overflow = ""; }
  viewer.addEventListener("click", function (e) { if (e.target === viewer) closeDoc(); });
  viewer.querySelector("[data-vclose]").addEventListener("click", closeDoc);
  viewer.querySelector("[data-vprint]").addEventListener("click", function () {
    // Same-origin PDFs print in place; cross-origin (CDN) docs block frame access,
    // so fall back to opening the file in a new tab for the browser's own print UI.
    try { vFrame.contentWindow.focus(); vFrame.contentWindow.print(); }
    catch (e) { if (currentUrl) window.open(currentUrl, "_blank", "noopener"); }
  });
  document.addEventListener("keydown", function (e) { if (e.key === "Escape" && !viewer.hidden) closeDoc(); });

  /* ---- certificate badges + packet + any [data-doc] ---- */
  root.querySelectorAll("[data-cert]").forEach(function (btn) {
    btn.addEventListener("click", function () { openDoc(btn.dataset.docUrl, btn.dataset.certTitle, btn.dataset.certSub); });
  });
  root.querySelectorAll("[data-doc]").forEach(function (btn) {
    btn.addEventListener("click", function () { openDoc(btn.dataset.docUrl, btn.dataset.docTitle, btn.dataset.docSub); });
  });

  /* ---- icons ---- */
  var DL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M12 3v12"/><path d="M7 11l5 5 5-5"/><path d="M5 21h14"/></svg>';
  var ARR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M5 12h13"/><path d="M13 6l6 6-6 6"/></svg>';

  /* ---- filtering ---- */
  function applyFilter() {
    var q = query.trim().toLowerCase();
    filtered = ALL.filter(function (p) {
      if (q && p.title.toLowerCase().indexOf(q) === -1) return false;
      if (cats.length && cats.indexOf(p.category) === -1) return false;
      return true;
    });
    rendered = 0;
    listEl.innerHTML = "";
    renderBatch();
    countEl.innerHTML = "Showing <strong>" + filtered.length + "</strong> of " + ALL.length + " documents";
    clearEl.hidden = !(q || cats.length);
    buildAZ();
  }

  function groupKey(name) { var c = name[0].toUpperCase(); return /[A-Z]/.test(c) ? c : "#"; }

  function renderBatch() {
    var slice = filtered.slice(rendered, rendered + BATCH);
    var lastLetter = lastRenderedLetter();
    var frag = document.createDocumentFragment();
    var curGroup = listEl.querySelector(".le-group:last-of-type");
    slice.forEach(function (p) {
      var L = groupKey(p.title);
      if (L !== lastLetter) {
        var head = document.createElement("div");
        head.className = "le-letter"; head.dataset.letter = L;
        head.innerHTML = '<span>' + L + '</span><i></i>';
        frag.appendChild(head);
        curGroup = document.createElement("div"); curGroup.className = "le-group";
        frag.appendChild(curGroup);
        lastLetter = L;
      }
      curGroup.appendChild(rowEl(p));
    });
    listEl.appendChild(frag);
    rendered += slice.length;
    if (loadmoreWrap) loadmoreWrap.hidden = rendered >= filtered.length;
    if (filtered.length === 0) {
      var icon = LOGO_ICON ? '<div class="le-empty__icon"><img src="' + LOGO_ICON + '" alt="" width="84" height="84" loading="lazy"></div>' : "";
      listEl.innerHTML = '<div class="le-empty">' + icon + '<b>No documents match</b><p>Try a different product name, or clear your filters. Need a document we don\'t list? Contact our team.</p><button type="button" class="le-btn le-btn--gold" data-clear>Clear all filters</button></div>';
      if (loadmoreWrap) loadmoreWrap.hidden = true;
    }
  }
  function lastRenderedLetter() {
    var heads = listEl.querySelectorAll(".le-letter");
    return heads.length ? heads[heads.length - 1].dataset.letter : null;
  }

  /* ---- category pill(s): colored from the metaobject map (text + border + fill) ---- */
  function catPill(name) {
    if (!name) return "";
    var col = catColor(name);
    var style = "color:" + col + ";border-color:" + hexToRgba(col, 0.55) + ";background:" + hexToRgba(col, 0.13) + ";";
    return '<span class="le-cattag" style="' + style + '">' + escapeHtml(name) + "</span>";
  }
  /* ---- CDT/BDT variant chip for Flavor Enhanced split rows ---- */
  function variantChip(label) {
    if (!label) return "";
    var col = catColor(label); // acronym-keyed (CDT/BDT) in the map
    var style = "color:" + col + ";border-color:" + hexToRgba(col, 0.55) + ";background:" + hexToRgba(col, 0.13) + ";";
    return '<span class="le-vchip" style="' + style + '">' + escapeHtml(label) + "</span>";
  }

  function rowEl(p) {
    var row = document.createElement("div");
    row.className = "le-row";
    var url = p.url || (PRODUCT_BASE + "/" + p.handle);
    var docTitle = p.title + (p.variantLabel ? " (" + p.variantLabel + ")" : "");
    row.innerHTML =
      '<div class="le-row-main">' +
        '<a class="le-pname" href="' + encodeURI(url) + '" target="_blank" rel="noopener">' + escapeHtml(p.title) + '</a>' +
        '<div class="le-tagrow">' + catPill(p.category) + variantChip(p.variantLabel) + '</div>' +
      '</div>' +
      '<div class="le-row-actions">' +
        (p.coaUrl ? '<button type="button" class="le-doc le-doc--coa" data-open="' + encodeURI(p.coaUrl) + '" data-t="' + escapeAttr(docTitle) + '" data-s="Certificate of Analysis">' + DL + 'COA</button>' : "") +
        (p.sdsUrl ? '<button type="button" class="le-doc le-doc--sds" data-open="' + encodeURI(p.sdsUrl) + '" data-t="' + escapeAttr(docTitle) + '" data-s="Safety Data Sheet">' + DL + 'SDS</button>' : "") +
        '<a class="le-view" href="' + encodeURI(url) + '" target="_blank" rel="noopener">View product' + ARR + '</a>' +
      '</div>';
    row.querySelectorAll("[data-open]").forEach(function (b) {
      b.addEventListener("click", function () { openDoc(b.getAttribute("data-open"), b.getAttribute("data-t"), b.getAttribute("data-s")); });
    });
    return row;
  }

  /* ---- A–Z rail ---- */
  var azEl;
  function buildAZ() {
    if (!SHOW_AZ) return;
    if (!azEl) {
      var wrap = document.createElement("div"); wrap.className = "le-listwrap";
      listEl.parentNode.insertBefore(wrap, listEl);
      wrap.appendChild(listEl);
      azEl = document.createElement("nav"); azEl.className = "le-az"; azEl.setAttribute("aria-label", "Jump to letter");
      wrap.appendChild(azEl);
    }
    var present = {};
    filtered.forEach(function (p) { present[groupKey(p.title)] = true; });
    azEl.innerHTML = "";
    "#ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").forEach(function (L) {
      var b = document.createElement("button");
      b.textContent = L; b.disabled = !present[L];
      b.addEventListener("click", function () { jump(L); });
      azEl.appendChild(b);
    });
  }
  function jump(L) {
    // ensure target rendered (windowing): render until the letter exists or list exhausted
    var guard = 0;
    while (rendered < filtered.length && !listEl.querySelector('.le-letter[data-letter="' + cssEsc(L) + '"]') && guard++ < 50) renderBatch();
    var head = listEl.querySelector('.le-letter[data-letter="' + cssEsc(L) + '"]');
    if (head) window.scrollTo({ top: head.getBoundingClientRect().top + window.scrollY - 140, behavior: "smooth" });
  }

  /* ---- category menu (alphabetical) ---- */
  function buildCats() {
    var set = {};
    ALL.forEach(function (p) { if (p.category) set[p.category] = true; });
    var list = Object.keys(set).sort();
    catMenu.innerHTML = "";
    list.forEach(function (c) {
      var b = document.createElement("button");
      b.type = "button"; b.className = "le-catopt"; b.setAttribute("aria-pressed", "false");
      var dot = '<i style="background:' + catColor(c) + ';border-color:' + catColor(c) + ';"></i>';
      b.innerHTML = dot + escapeHtml(c);
      b.addEventListener("click", function () {
        var i = cats.indexOf(c);
        if (i === -1) cats.push(c); else cats.splice(i, 1);
        b.setAttribute("aria-pressed", i === -1 ? "true" : "false");
        catCount.textContent = cats.length ? cats.length : "";
        applyFilter();
      });
      catMenu.appendChild(b);
    });
  }
  if (catToggle) catToggle.addEventListener("click", function () {
    var open = catMenu.hasAttribute("hidden");
    if (open) catMenu.removeAttribute("hidden"); else catMenu.setAttribute("hidden", "");
    catToggle.setAttribute("aria-expanded", open ? "true" : "false");
  });
  document.addEventListener("click", function (e) {
    if (catToggle && !root.querySelector("[data-catwrap]").contains(e.target)) {
      catMenu.setAttribute("hidden", ""); catToggle.setAttribute("aria-expanded", "false");
    }
  });

  /* ---- input + load more ---- */
  var dt;
  function syncSearchClear() {
    if (searchWrap) searchWrap.classList.toggle("has-value", !!(searchEl && searchEl.value));
  }
  function doClear() {
    query = ""; cats = [];
    if (searchEl) searchEl.value = "";
    syncSearchClear();
    if (catCount) catCount.textContent = "";
    if (catMenu) catMenu.querySelectorAll("[aria-pressed]").forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
    applyFilter();
  }
  if (searchEl) searchEl.addEventListener("input", function () {
    syncSearchClear();
    clearTimeout(dt); dt = setTimeout(function () { query = searchEl.value; applyFilter(); }, 140);
  });
  if (searchClear) searchClear.addEventListener("click", function () {
    if (searchEl) { searchEl.value = ""; searchEl.focus(); }
    query = ""; syncSearchClear(); applyFilter();
  });
  if (clearEl) clearEl.addEventListener("click", doClear);
  /* the no-match empty state injects its own [data-clear] reset button */
  if (listEl) listEl.addEventListener("click", function (e) {
    if (e.target.closest("[data-clear]")) doClear();
  });
  if (loadmoreBtn) loadmoreBtn.addEventListener("click", renderBatch);

  /* ---- helpers ---- */
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  function escapeAttr(s) { return escapeHtml(s); }
  function cssEsc(s) { return s === "#" ? "\\#" : s; }

  /* ---- load the index ---- */
  function boot(data) {
    // Sort by title, then variantLabel (so a Flavor Enhanced product's CDT + BDT
    // rows sit together in a stable, alphabetical order).
    ALL = (data || []).slice().sort(function (a, b) {
      var t = a.title.localeCompare(b.title);
      return t !== 0 ? t : String(a.variantLabel || "").localeCompare(String(b.variantLabel || ""));
    });
    buildCats();
    applyFilter();
  }

  var src = root.dataset.indexSource;
  if (src === "asset" && root.dataset.indexUrl) {
    fetch(root.dataset.indexUrl, { cache: "force-cache" })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(boot)
      .catch(function (err) {
        countEl.textContent = "Could not load the document index.";
        console.error("[compliance] index load failed:", err);
      });
  } else if (src === "predictive") {
    /* Live mode: query Shopify predictive search on input instead of a static index.
       Implement against /search/suggest.json?q=…&resources[type]=product or the
       Storefront API `predictiveSearch`. Map each product's COA/SDS metafields into
       the same row shape and call openDoc(url, title). Left as a documented stub —
       the static-asset path above is recommended for 600+ rarely-changing SKUs. */
    countEl.textContent = "Predictive-search mode: wire /search/suggest.json (see README).";
  } else {
    countEl.textContent = "No index source configured.";
  }
})();
